import React from "react";
import { ChangesetReason } from '../../../../types';
import {
	designChangeMapSettingsGlobalLoopHandlingLabels,
	designChangeMapSettingsIntentHandlingLabels,
	designChangeMapSettingsLabels
} from "../../../../utilities";
import Utility from "../../../../utilities/Utility";
import { ChangeLogItem } from "./change-log-item";
import style from './style/change-log-item.module.scss';

const dotProp = require('dot-prop');

/**
 * React component to display changeMap data based on two versions of design.json
 *
 * @class ChangesContainer
 * @extends {React.Component}
 */
export default class ChangesContainer extends React.Component {
	constructor(props) {
		super(props); // changeMap
		this.getChangeItem = this.getChangeItem.bind(this);
	}

	getHeaderByType(type) {
		switch (type) {
			case "modules":
				return this.getModuleChangeHeader();
			default:
				break;
		}
	}

	getListByType(type) {
		switch (type) {
			case "modules":
				return this.getModuleItems();
			case "application-modules":
			case "application-variables":
			case "application-objects":
			case "application-intents":
				return this.getApplicationComponentsItems();
			case "functions":
				return this.getFunctionsItems();
			case "folders":
				return this.getModuleFolderItems();
			case "fillerPrompts":
				return this.getFillerPromptsList();
			case "globalIntents":
				return this.getGlobalIntentsList();
			case 'compoundIntents':
				return this.getCompoundIntentsList();
			case "graphQLSchemas":
				return this.getGraphQLSchemasList();
			case "csvData":
				return this.getCSVDataList();
			case 'ccxml':
				return this.getCCXMLList();
			case 'appProperties':
				return this.getAppPropertiesItems();
			case "languages":
				return this.getLanguagesList();
			case "applicationVersion":
				return this.getApplicationVersionList();
			case "settings":
			case "globalErrorHandling":
			case "globalExceptionHandling":
			case "description":
			case "globalLoopHandling":
			case "inARow":
			case "xAndOut":
			case "intentHandling":
			case "inARowLimit":
			case "xAndOutLimit":
			case "omniChannel":	
			case "sensitiveOverrides":	
			case "noInputTimeout":
			case "dtmf_live_agent_override_pattern":
			case "rootSettings":
				return this.getSettingsList();
			case "unitTests":
				return this.getUnitTestsItems();
            case "dialogs":
				return this.getDialogsItems();
			case "branchingMap":
				return this.getBranchingMapItems();
			case "branching":
				return this.getBranchingItems();
		}
	}

	/**
	 * Get name of deleted module from change maps
	 * 
	 * @param {string} modId
	 * @returns {string} module name 
	 */
	getDeletedApplicationsModuleName(modId) {
		const cmaps = window.apm.changeLogMaps || [];
		let name = 'UNKNOWN';

		cmaps.forEach(cm => {
			if (dotProp.get(cm.changeLogMap[0]['application-modules'])) {
				Object.keys(cm.changeLogMap[0]['application-modules']).forEach(key => {
					if (key === modId) {
						if (cm.changeLogMap[0]['application-modules'][key].reason === 'Deleted') {
							name = cm.changeLogMap[0]['application-modules'][key].node.name;
						}
					}
				});
			}
		});

		return name;
	}

	/**
	 * Get Module header
	 *
	 * @returns JSX
	 */
	getModuleChangeHeader() {
		// if module was deleted, then wont be on apm.getNode, find it in change maps
		const name = window.apm.getNode(this.props.changeMap[0])
			? window.apm.getNode(this.props.changeMap[0]).name
			: this.getDeletedApplicationsModuleName(this.props.changeMap[0]);

		const uuid = Utility.uuid4();

		return (
			<h5 key={uuid} className="moduleHeader">
				<i className={"fe icon-package"} />
				<span style={{ paddingLeft: "6px" }} title={this.props.changeMap[0]}>{name}</span>
			</h5>
		);
	}

	/**
	 * Generic list item for changelog UI
	 * @param {object} item data object 
	 */
	getChangeItem(props) {
		const uuid = Utility.uuid4();
		return (
			<ChangeLogItem {...props} key={uuid} />
		);
	}

	getModuleItems() {
		const mod = this.props.serialized
			? this.props.serialized.components.modules.find(
				m => m.id === this.props.changeMap[0]
			)
			: this.getDeletedApplicationsModuleName(this.props.changeMap[0]);


		let nodeItems = [];
		let linkItems = [];
		let localVariableItems = [];
		let noteText = item =>
			item.node.props.config.note
				? item.node.props.config.note.substring(0, 10)
				: "";

		const noteName = item => {
			// TODO: note id in string for dev debug: remove this!
			return `Note: ${noteText(item)}`;
		};

		if (this.props.changeMap[1].nodes) {
			nodeItems = this.props.changeMap[1].nodes.sort((a,b) => a.node.name < b.node.name ? 1 : -1).sort((a,b) => a.node._class > b.node._class ? 1 : -1).map(item => {
				const name =
					item.node._class === "NoteNodeModel"
						? noteName(item)
						: item.node.name;
				const changeLabel = item.reason;
				const iconClass = item.node.props.iconClass;
				
				return this.getChangeItem({
					item,
					name,
					iconClass,
					changeLabel
				});
			});
		}

		if (this.props.changeMap[1].links) {

			const clonedLinks = _.cloneDeep(this.props.changeMap[1].links);
			clonedLinks.forEach(item => {
				item.link.props.config.name = item.link.props.config.name || "UN_NAMED";
			});
			
			linkItems = clonedLinks.sort((a,b) => 
				a.link.props.config.name === b.link.props.config.name ? 0 :
				(a.link.props.config.name > b.link.props.config.name ? 1 : -1)
			).map(item => {
				let itemText = item.link.props.config.name || "UN_NAMED";
				const changeLabel = item.reason;
				const iconClass = "link";
				if (mod) {
					const source = mod.versions[1].flow.nodes.find(
						node => node.id === item.link.source
					);
					const target = mod.versions[1].flow.nodes.find(
						node => node.id === item.link.target
					);
					if (source && target) {
						itemText += " from " + source.name + " to " + target.name;
					} else {
						if (source) {
							itemText += " from " + source.name;
						} else if (target) {
							itemText += " to " + target.name;
						}
					}
				}

				return this.getChangeItem({
					item, 
					name: itemText,
					iconClass,
					changeLabel
				});
			});
		}

		if (this.props.changeMap[1].localVariables) {
			localVariableItems = this.props.changeMap[1].localVariables.sort((a,b) => a.object.name > b.object.name ? 1 : -1).map(
				item => {
					const name = item.object.name || "UN_NAMED";
					const changeLabel = item.reason;
					const iconClass = item.object.props.iconClass;

					return this.getChangeItem({
						item,
						name,
						iconClass,
						changeLabel
					});
				}
			);
		}

		return nodeItems.concat(linkItems).concat(localVariableItems);
	}

	getApplicationComponentsItems() {
		const item = this.props.changeMap[1];
		const name = this.props.changeMap[1].node.name || "UN_NAMED";
		const changeLabel = this.props.changeMap[1].reason;
		const iconClass = this.props.changeMap[1].node.props.iconClass;

		return this.getChangeItem({ item, name, iconClass, changeLabel });
	}

	getFunctionsItems() {
		const item = this.props.changeMap[1];
		const name = this.props.changeMap[1].object.name || "UN_NAMED";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getModuleFolderItems() {
		const item = this.props.changeMap[1];
		const name = this.props.changeMap[1].object.name || "UN_NAMED";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item,
			name,
			iconClass: null,
			changeLabel,
		});
	}

	getGlobalIntentsList() {
		var name = "UN_NAMED";
		var iconClass = "radio";
		if (this.props.changeMap[1].object.condition) {
			name = this.props.changeMap[1].object.conditionProperties.props.config.name !== "" ? this.props.changeMap[1].object.conditionProperties.props.config.name : "UN_NAMED";
			iconClass = "link";
		}
		else if (this.props.changeMap[1].object.intent) {
			const intent = window.apm.serialized.components.intents.find(
				i => i.id === this.props.changeMap[1].object.intent
			);
			name = intent ? intent.name : "UN_NAMED";
			iconClass = "radio";
		}
		const changeLabel = this.props.changeMap[1].reason;
		const item = this.props.changeMap[1];

		return this.getChangeItem({
			item,
			name,
			iconClass: iconClass,
			changeLabel
		});
	}

	getCompoundIntentsList() {
		const fromIntent1 = window.apm.serialized.components.intents.find(i => i.id === this.props.changeMap[1].object.fromIntent1);
		const fromIntent2 = window.apm.serialized.components.intents.find(i => i.id === this.props.changeMap[1].object.fromIntent2);
		const toIntent = window.apm.serialized.components.intents.find(i => i.id === this.props.changeMap[1].object.toIntent);

		const changeLabel = this.props.changeMap[1].reason;

		return (<div id={Utility.uuid4()}>
			<table style={{ width: "98%", margin: "10px" }} >
				<thead>
					<tr>
						<th style={{width: '25%'}}>From Intent 1</th>
						<th style={{width: '25%'}} >From Intent 2</th>
						<th style={{width: '25%'}} >To Intent</th>
						<th style={{width: '25%'}} >Reason</th>
					</tr>
				</thead>
				<tbody>
					<tr >
						<td>{fromIntent1 ? fromIntent1.name : "???"}</td>
						<td>{fromIntent2 ? fromIntent2.name : "???"}</td>
						<td>{toIntent ? toIntent.name : "???"}</td>
						<td>{changeLabel}</td>
					</tr>


				</tbody>
			</table>

		</div>)
		// TODO:
		//return this.getChangeItem({ name, iconClass: null, changeLabel });
	}

	getFillerPromptsList() {
		const label = this.props.changeMap[0].toUpperCase();
		const prompts = this.props.changeMap[1];

		return (
			<div
				style={{ margin: "0 20px" }}
				key={Utility.uuid4()}>
				<div style={{ padding: "4px", width: "80%" }}>
					<i className={"fe icon-message-square"} />
					<span style={{ paddingLeft: "4px" }}>{label}</span>
				</div>
				{Object.keys(prompts).map(prompt => {
					const name = prompts[prompt].object.name || "UN_NAMED";
					const changeLabel = prompts[prompt].reason;

					return this.getChangeItem({
						item: null,
						name,
						iconClass: null,
						changeLabel
					});
				})}
			</div>
		);
	}

	getGraphQLSchemasList() {
		const schema = this.props.changeMap[1];
		const name = schema.object.name || "UN_NAMED";
		const changeLabel = schema.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getCSVDataList() {
		const csv = this.props.changeMap[1];
		const name = csv.object.name || "UN_NAMED";
		const changeLabel = csv.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getCCXMLList() {
		const name = "CCXML";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getAppPropertiesItems() {
		const name = this.props.changeMap[1].object.config || "UN_NAMED";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getGlobalExceptionHandlingItems() {
		const exceptionHandlers = {
			exception: "Process Engine Exception or Error",
			timeout: "Timeout Handler"
		};

		const changeset = this.props.changeMap.changeset;
		if(!changeset){
			return;
		}
		const changeItems = changeset.map(cs => {
			if(exceptionHandlers[cs.key]){
				return this.getChangeItem({
					item: null,
					name: exceptionHandlers[cs.key[0]],
					iconClass: null,
					changeLabel: this.props.changeMap.reason
				}); 
			}	
		});

		return changeItems;
	}
	
	getGlobalLoopHandlingItems() {
		const changeset = this.props.changeMap.changeset;
		if(!changeset){
			return;
		}

		const loopHandlers = {
			inARow: "Module for In-A-Row Handling",
			xAndOut: "Module for X-And-Out Handling"
		};

		const changeItems = changeset.map(cs => {
			if(loopHandlers[cs.key]){
				return this.getChangeItem({
					item: null,
					name: loopHandlers[cs.key[0]],
					iconClass: null,
					changeLabel: this.props.changeMap.reason
				}); 
			}	
		});

		return changeItems;
	}

	getIntentHandlingItems() {
		const changeset = this.props.changeMap.changeset;
		
		if(!changeset){
			return;
		}

		const handlers = {
			inARowLimit: "In-a-row count",
			xAndOutLimit: "X-and-out count",
		};
		const changeItems = changeset.sort((a,b) => a.key > b.key ? 1 : -1).map(cs => {
			if(handlers[cs.key]){
				return this.getChangeItem({
					item: null,
					name: handlers[cs.key[0]],
					iconClass: null,
					changeLabel: this.props.changeMap.reason
				}); 
			}	
		});

		return changeItems;
	}

	getLanguagesList() {
		const name = "Languages";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getApplicationVersionList() {
		const name = "Dependency Version";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}
	
	getSettingsList() {
		const getName = (key) => {
			if (key in designChangeMapSettingsLabels) {
				return designChangeMapSettingsLabels[key];
			} else if (key in designChangeMapSettingsIntentHandlingLabels) {
				return designChangeMapSettingsIntentHandlingLabels[key];
			} else if (key in designChangeMapSettingsGlobalLoopHandlingLabels) {
				return designChangeMapSettingsGlobalLoopHandlingLabels[key];
			}
		}
		return this.getChangeItem({
			name: getName(this.props.listType),
			iconClass: null,
			changeLabel: ChangesetReason.modified,
		});		
	}

	getUnitTestsItems() {
		const name = this.props.changeMap[1].object.name || "UN_NAMED";
		const changeLabel = this.props.changeMap[1].reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getDialogsItems() {
			const name = this.props.changeMap[1].object.name || "UN_NAMED";
			const changeLabel = this.props.changeMap[1].reason;

			return this.getChangeItem({
					item: null,
					name,
					iconClass: null,
					changeLabel
			});
	}
	
	getBranchingMapItems(){
		// merge, init-branchingMap
		if(this.props.changeMap[1] && this.props.changeMap[1][0].changeset){
			const type = this.props.changeMap[1][0].changeset[0].key;
			const changesetValues = (type === 'merge') ?
				 this.props.changeMap[1][0].changeset[0].value :
				 this.props.changeMap[1][0].changeset[0].value[0];
			const name = changesetValues.branch_name;
			const branchMergedLabel = "Branch " + changesetValues.branch_name + " merged at version #" + changesetValues.merged_at;
			const branchCreatedLabel = "Branch " + name + " created at version " + changesetValues.branched_at;  
			const iconClass = (type === 'merge') ? 'git-merge' : 'git-branch';
			
			return this.getChangeItem({
				item: null,
				name: '',
				iconClass,
				changeLabel: (type === 'merge') ? branchMergedLabel : branchCreatedLabel
			});
		}
	}

	getBranchingItems(){
		// pull, init (feature)
		const branchEvent = this.props.changeMap[1].changeset[0].value;
		const name = this.props.serialized.application;
		const pullLabel = 'Pulled from ' + branchEvent['parent_application'] + ' at version ' + branchEvent['pulled_at'];
		const branchedLabel = 'This branch created from ' + branchEvent['parent_branch_name'] + ' at version ' + branchEvent['branched_at'];
		const type = this.props.changeMap[1].changeset[0].value.type;
		const iconClass = (type === 'pull') ? 'git-merge' : 'git-branch';

		return this.getChangeItem({
			item: null,
            name: '',
            iconClass,
            changeLabel: (branchEvent.type === 'pull') ? pullLabel : branchedLabel
        });
	}

	render() {
		return (
			<div key={Utility.uuid4()} className={style.changeLogSet}>
				{this.getHeaderByType(this.props.listType)}

				{this.getListByType(this.props.listType)}
			</div>
		);
	}
}
